import React from "react";
import { useLocation, Link } from "react-router-dom";
import { openConsentSettings } from "./ConsentBanner";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  // Section anchors only. Route links are plain <Link>s and need no handler.
  // Returning without preventDefault lets the browser follow the real href,
  // which is what keeps these crawlable (audit T1).
  const handleSectionClick = (e, action) => {
    if (location.pathname !== "/") return; // let the anchor navigate home + hash
    e.preventDefault();
    document.getElementById(action)?.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = [
    { label: "Services",  action: "services",  href: "/#services"  },
    { label: "Portfolio", action: "portfolio", href: "/portfolio"  },
    { label: "Craft",     action: "craft",     href: "/craft"      },
    { label: "FAQ",       action: "faq",       href: "/faq"        },
    { label: "About",     action: "about",     href: "/#about"     },
    { label: "Careers",   action: "careers",   href: "/careers"    },
    { label: "Contact",   action: "contact",   href: "/#contact"   },
  ];

  // Legal links + the cookie-settings trigger live in their own row (below)
  // so the primary nav row doesn't have to fit them too — see audit T-footer-overflow.
  const legalLinks = [
    { label: "Privacy",   action: "privacy",   href: "/privacy"    },
    { label: "Terms",     action: "terms",     href: "/terms"      },
    { label: "AI & data", action: "ai-data-policy", href: "/ai-data-policy" },
  ];

  const socialLinks = [
    { label: "LinkedIn", href: "https://linkedin.com/company/grassurntech" },
    { label: "GitHub",   href: "https://github.com/grassurntech"           },
    { label: "Email",    href: "mailto:hello@grassurntech.com"             },
  ];

  const baseLinkStyle = {
    fontFamily: "Montserrat, sans-serif",
    fontSize: "12px",
    fontWeight: 300,
    color: "rgba(255,255,255,0.72)",
    textDecoration: "none",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    transition: "color 0.2s",
    whiteSpace: "nowrap",
  };

  const divider = (
    <div
      className="footer-divider"
      style={{
        width: "1px",
        height: "16px",
        background: "rgba(255,255,255,0.10)",
        flexShrink: 0,
        margin: "0 32px",
      }}
    />
  );

  return (
    <footer
      style={{
        background: "#040808",
        borderTop: "1px solid rgba(91,148,144,0.08)",
      }}
    >
      <div
        className="footer-inner"
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "16px 40px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* Row 1: logo/tagline, primary nav, social links.
            flexWrap + rowGap let this row wrap onto a second line instead of
            overflowing when the combined content is wider than the container
            (audit: sitewide horizontal scrollbar at 768-1440px). */}
        <div
          className="footer-row-main"
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: "14px",
            minHeight: "44px",
          }}
        >
          {/* Logo + tagline */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <img
              src="/grassurn-icon.png"
              alt="Grassurn Tech"
              style={{
                width: "36px",
                height: "30px",
                objectFit: "contain",
                filter: "brightness(0) invert(1)",
                opacity: 0.75,
              }}
            />
            <span
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "16.6px",
                fontWeight: 300,
                color: "rgba(255,255,255,0.40)",
                whiteSpace: "nowrap",
              }}
            >
              Ship it. Govern it. Trust it.
            </span>
          </div>

          {/* Push everything else to the right */}
          <div className="footer-spacer" style={{ flex: 1 }} />

          {/* Nav links */}
          <div
            className="footer-nav-links"
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px 28px" }}
          >
            {navLinks.map((link) => {
              const hoverProps = {
                onMouseEnter: (e) => (e.currentTarget.style.color = "#7BB5B1"),
                onMouseLeave: (e) => (e.currentTarget.style.color = "rgba(255,255,255,0.72)"),
              };

              return link.href.startsWith("/#") ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleSectionClick(e, link.action)}
                  style={baseLinkStyle}
                  {...hoverProps}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  style={baseLinkStyle}
                  {...hoverProps}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {divider}

          {/* Social links */}
          <div
            className="footer-social-links"
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px 28px" }}
          >
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                style={baseLinkStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#7BB5B1")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.72)")}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Row 2: legal links + cookie settings (left), copyright (right).
            Kept separate from row 1 so the legal/consent controls this
            branch added never compete with the primary nav for width. */}
        <div
          className="footer-row-legal"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            rowGap: "10px",
            paddingTop: "14px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div
            className="footer-legal-links"
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px 24px" }}
          >
            {legalLinks.map((link) => (
              <Link key={link.label} to={link.href} style={baseLinkStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#7BB5B1")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.72)")}
              >
                {link.label}
              </Link>
            ))}

            {/* Reopens the analytics consent banner (audit T13) */}
            <button
              type="button"
              onClick={openConsentSettings}
              style={baseLinkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#7BB5B1")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.72)")}
            >
              Cookie settings
            </button>
          </div>

          {/* Copyright */}
          <span
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "11px",
              fontWeight: 300,
              color: "rgba(255,255,255,0.25)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            © {currentYear} Grassurn Technologies
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
